'use client'

import { MigrationStatusReporter } from '@/components/auth/migration-status-reporter'
import {
	PRIVY_APP_ID,
	PRIVY_CLIENT_ID,
	alchemyMigrationConfig,
	privyClientConfig,
} from '@/config/privy'
import { isLegacyBrokenSafeUser } from '@/constants/legacy-broken-safes'
import { BASE_RPC_URL } from '@/constants/limitless'
import { POLYGON_RPC_URL } from '@/constants/polymarket'
import { authFetch } from '@/lib/auth/auth-fetch'
import type { ViemEthersSigner } from '@/lib/wallet-core/ethers'
import { createEvmWalletClients, createPolygonEthersSigner } from '@/lib/wallet-core/evm'
import { useSolanaWalletCore } from '@/lib/wallet-core/solana'
import { WalletContext } from '@/providers/WalletContext'
import type { PolymarketActiveWallet } from '@/utils/polymarket-wallet-address'
import { captureApiError } from '@/utils/sentry-helpers'
import { MigrationProvider } from '@privy-io/alchemy-migration'
import '@privy-io/alchemy-migration/styles.css'
import {
	type ConnectedWallet,
	PrivyProvider,
	toViemAccount,
	useCreateWallet,
	usePrivy,
	useWallets,
} from '@privy-io/react-auth'
import { useCreateWallet as useCreateSolanaWallet } from '@privy-io/react-auth/solana'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
	http,
	type LocalAccount,
	type PublicClient,
	type WalletClient,
	createPublicClient,
} from 'viem'
import { base, polygon } from 'viem/chains'

const publicClient = createPublicClient({
	chain: polygon,
	transport: http(POLYGON_RPC_URL),
})

const basePublicClient = createPublicClient({
	chain: base,
	transport: http(BASE_RPC_URL),
}) as unknown as PublicClient

// The SDK's getEmbeddedConnectedWallet excludes imported wallets — but every
// key migrated from Alchemy arrives via Privy's import API. Accept both.
function findEmbeddedWallet(wallets: ConnectedWallet[]): ConnectedWallet | null {
	return (
		wallets.find(
			wallet =>
				wallet.walletClientType === 'privy' &&
				(wallet.connectorType === 'embedded' || wallet.connectorType === 'embedded_imported')
		) ?? null
	)
}

const MAX_WALLET_BOOTSTRAP_ATTEMPTS = 3
const WALLET_BOOTSTRAP_RETRY_DELAY_MS = 2_000

interface BootstrapAttempts {
	userId: string
	attempt: number
	done: boolean
}

/**
 * Headless logins do not auto-create embedded wallets, and users pending
 * Alchemy key migration must not get fresh wallets (that would brick the
 * transfer). Creates ETH+SOL wallets only for users the migration SDK does
 * not own. Failures report to Sentry and retry up to
 * MAX_WALLET_BOOTSTRAP_ATTEMPTS times per user.
 *
 * External-wallet users whose keys were bulk-imported into Privy before
 * login already have embedded ETH/SOL wallets, so the hasEth/hasSol guard
 * below short-circuits creation for them (the bulk-import job also sets the
 * alchemy_org_id skip-marker as a second line of defense).
 */
function EmbeddedWalletBootstrap() {
	const { ready, authenticated, user } = usePrivy()
	const { createWallet: createEthWallet } = useCreateWallet()
	const { createWallet: createSolanaWallet } = useCreateSolanaWallet()
	const [attempts, setAttempts] = useState<BootstrapAttempts | null>(null)
	const inFlightRef = useRef(false)
	const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		return () => {
			if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
		}
	}, [])

	useEffect(() => {
		if (!(ready && authenticated && user)) return
		// Users exported from Alchemy carry alchemy_org_id metadata; the
		// MigrationProvider transfers their existing keys instead.
		if (user.customMetadata?.alchemy_org_id !== undefined) return
		// StrictMode safety: never start a second attempt while one is in flight.
		if (inFlightRef.current) return

		const state = attempts?.userId === user.id ? attempts : null
		if (state?.done) return
		const attempt = (state?.attempt ?? 0) + 1
		if (attempt > MAX_WALLET_BOOTSTRAP_ATTEMPTS) return

		const userId = user.id
		const hasEth = user.linkedAccounts.some(
			account =>
				account.type === 'wallet' &&
				account.chainType === 'ethereum' &&
				account.walletClientType === 'privy'
		)
		const hasSol = user.linkedAccounts.some(
			account =>
				account.type === 'wallet' &&
				account.chainType === 'solana' &&
				account.walletClientType === 'privy'
		)

		if (hasEth && hasSol) {
			setAttempts({ userId, attempt: attempt - 1, done: true })
			return
		}

		// A stale retry timer from a previous failure would regress the
		// attempt counter — drop it before recording the new attempt.
		if (retryTimerRef.current) {
			clearTimeout(retryTimerRef.current)
			retryTimerRef.current = null
		}
		inFlightRef.current = true
		setAttempts({ userId, attempt, done: false })

		async function bootstrap() {
			try {
				if (!hasEth) await createEthWallet()
				if (!hasSol) await createSolanaWallet()
				setAttempts({ userId, attempt, done: true })
			} catch (err) {
				console.error('Embedded wallet bootstrap failed:', err)
				captureApiError(err, {
					endpoint: 'embedded_wallet_bootstrap',
					tags: { category: 'auth' },
					extra: { userId, attempt },
				})
				// Re-setting the same attempt with a fresh object identity
				// re-runs the effect, which then proceeds to attempt + 1.
				retryTimerRef.current = setTimeout(() => {
					setAttempts({ userId, attempt, done: false })
				}, WALLET_BOOTSTRAP_RETRY_DELAY_MS)
			} finally {
				inFlightRef.current = false
			}
		}
		bootstrap()
	}, [ready, authenticated, user, attempts, createEthWallet, createSolanaWallet])

	return null
}

function WalletContextProvider({ children }: { children: ReactNode }) {
	const [walletClient, setWalletClient] = useState<WalletClient | null>(null)
	const [baseWalletClient, setBaseWalletClient] = useState<WalletClient | null>(null)
	const [evmAccount, setEvmAccount] = useState<LocalAccount | null>(null)
	const [eoaAddress, setEoaAddress] = useState<`0x${string}` | undefined>(undefined)
	const [ethersSigner, setEthersSigner] = useState<ViemEthersSigner | null>(null)
	const [userId, setUserId] = useState<string | null>(null)
	const [polymarketSafe, setPolymarketSafe] = useState<string | null>(null)
	const [polymarketActiveWallet, setPolymarketActiveWallet] =
		useState<PolymarketActiveWallet | null>(null)
	const [isPolymarketSafeResolved, setIsPolymarketSafeResolved] = useState(false)

	const { ready, authenticated } = usePrivy()
	const { wallets } = useWallets()
	const embeddedWallet = findEmbeddedWallet(wallets)
	const {
		signer: solanaSigner,
		connection: solanaConnection,
		address: solanaAddress,
		isReady: isSolanaReady,
	} = useSolanaWalletCore()

	useEffect(() => {
		async function fetchPolymarketSafe() {
			if (!(ready && authenticated)) {
				setUserId(null)
				setPolymarketSafe(null)
				setPolymarketActiveWallet(null)
				// Unauthenticated state IS resolved — there's nothing to fetch.
				// Without this, downstream initialisers stay blocked forever in
				// the logged-out shell.
				setIsPolymarketSafeResolved(true)
				return
			}
			// Reset to "not yet resolved" on every fetch so consumers re-block
			// when auth changes (e.g. user switches accounts).
			setIsPolymarketSafeResolved(false)
			try {
				const res = await authFetch('/api/user/profile', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({}),
				})
				if (res.ok) {
					const data = await res.json()
					const id: string | null = data.user?.id ?? null
					setUserId(id)
					// Force-null polymarketSafe for users on the broken-Safe allow-list
					// so every downstream consumer with a `?? deriveDepositWallet(eoa)`
					// fallback routes them to the deposit wallet path.
					setPolymarketSafe(isLegacyBrokenSafeUser(id) ? null : (data.user?.polymarketSafe ?? null))
					const activeWallet = data.user?.polymarketActiveWallet
					setPolymarketActiveWallet(
						activeWallet === 'safe' || activeWallet === 'deposit' ? activeWallet : null
					)
				}
				// On non-OK, leave userId/polymarketSafe at their current values and
				// resolve the gate below — initialisers will take the deposit-wallet
				// path, which is the correct fallback for users without a Safe and
				// an acceptable best-effort for users whose profile fetch
				// transient-failed.
			} catch {
				// Silently fail — polymarketSafe stays null, deposit-wallet path used
			} finally {
				setIsPolymarketSafeResolved(true)
			}
		}
		fetchPolymarketSafe()
	}, [ready, authenticated])

	useEffect(() => {
		let cancelled = false

		async function init() {
			if (!(ready && authenticated && embeddedWallet)) {
				setEvmAccount(null)
				setWalletClient(null)
				setBaseWalletClient(null)
				setEthersSigner(null)
				setEoaAddress(undefined)
				return
			}

			try {
				const account = (await toViemAccount({ wallet: embeddedWallet })) as LocalAccount
				if (cancelled) return

				const clients = createEvmWalletClients(account)
				const signer = await createPolygonEthersSigner(clients.walletClient)
				if (cancelled) return

				setEvmAccount(account)
				setEoaAddress(account.address)
				setWalletClient(clients.walletClient)
				setBaseWalletClient(clients.baseWalletClient)
				setEthersSigner(signer)
			} catch (err) {
				console.error('Failed to initialize wallet client:', err)
				captureApiError(err, {
					endpoint: 'wallet_provider_init',
					tags: { category: 'auth' },
					extra: { address: embeddedWallet?.address },
				})
				if (cancelled) return
				setEvmAccount(null)
				setWalletClient(null)
				setBaseWalletClient(null)
				setEthersSigner(null)
				setEoaAddress(undefined)
			}
		}
		init()

		return () => {
			cancelled = true
		}
	}, [ready, authenticated, embeddedWallet])

	return (
		<WalletContext.Provider
			value={{
				eoaAddress,
				evmAccount,
				walletClient,
				publicClient,
				ethersSigner,
				baseWalletClient,
				basePublicClient,
				solanaAddress,
				solanaSigner,
				solanaConnection,
				isReady: ready && authenticated && !!walletClient && !!eoaAddress,
				isSolanaReady,
				isBaseReady: ready && authenticated && !!baseWalletClient && !!eoaAddress,
				authenticated: ready && authenticated && !!embeddedWallet,
				userId,
				polymarketSafe,
				isPolymarketSafeResolved,
				polymarketActiveWallet,
				setPolymarketActiveWallet,
			}}
		>
			{children}
		</WalletContext.Provider>
	)
}

export function WalletProvider({ children }: { children: ReactNode }) {
	return (
		<PrivyProvider appId={PRIVY_APP_ID} clientId={PRIVY_CLIENT_ID} config={privyClientConfig}>
			<MigrationProvider
				alchemyConfig={alchemyMigrationConfig}
				privyAppId={PRIVY_APP_ID}
				privyClientId={PRIVY_CLIENT_ID}
			>
				<MigrationStatusReporter />
				<EmbeddedWalletBootstrap />
				<WalletContextProvider>{children}</WalletContextProvider>
			</MigrationProvider>
		</PrivyProvider>
	)
}
